/**
 * Desk Browse's filter column: what the Where list offers, and which filters
 * are on. Dependency-free apart from `src/districts.ts` (itself
 * dependency-free), so the Worker's suite tests it (`test/deskFilters.test.ts`).
 */
import { BEZIRKE, ORTSTEILE, toOrtsteil } from '../../../src/districts.ts'
import { DESK_DEFAULTS, type DeskFilters } from './deskRoute.ts'

/** A `districts` row (C0.1, D6): a neighbourhood and the group it folds into —
 *  a Bezirk in Berlin, a town in Boston. A null `bezirk` is a place that is
 *  not in any group but has rows of its own: Berlin's `Potsdam`, which is why
 *  there is no Potsdam special case here any more — folding it into nothing
 *  would leave Sanssouci reachable only by Anywhere, and the row says so. */
export interface DistrictRow { ortsteil: string; bezirk: string | null }

/** Berlin's rows as `src/districts.ts` knows them, plus Potsdam: what the
 *  folds answer from when the caller has none (the Worker's suite, a screen
 *  that has not been given a region's rows). The live table has 103 Berlin
 *  rows to these 97. */
export const BERLIN_ROWS: readonly DistrictRow[] = [
  ...Object.entries(ORTSTEILE).map(([ortsteil, bezirk]): DistrictRow => ({ ortsteil, bezirk })),
  { ortsteil: 'Potsdam', bezirk: null },
]

interface FoldIndex { byOrtsteil: Map<string, string>; byBezirk: Map<string, string> }
const indexes = new WeakMap<readonly DistrictRow[], FoldIndex>()
function indexOf(rows: readonly DistrictRow[]): FoldIndex {
  let ix = indexes.get(rows)
  if (!ix) {
    ix = { byOrtsteil: new Map(), byBezirk: new Map() }
    for (const r of rows) {
      ix.byOrtsteil.set(r.ortsteil.toLowerCase(), r.bezirk ?? r.ortsteil)
      if (r.bezirk) ix.byBezirk.set(r.bezirk.toLowerCase(), r.bezirk)
    }
    indexes.set(rows, ix)
  }
  return ix
}

/**
 * a3-24 (Doug chose: Bezirke only). A row's `district` is whatever its venue
 * wrote — an Ortsteil (`Tiergarten`, `Prenzlauer Berg`), a Bezirk
 * (`Friedrichshain-Kreuzberg`) or a bare name that is both (`Mitte`) — so the
 * list mixed levels: Charlottenburg beside Charlottenburg-Wilmersdorf.
 * Everything folds to its Bezirk here; Potsdam is kept as itself; a name
 * `districts.ts` cannot place gives '' and is listed only under Anywhere.
 *
 * Also how an old `?district=Wedding` link is read: the Ortsteil it names
 * folds to `Mitte`, the Bezirk it is in, rather than to an empty list.
 */
export function whereOf(raw: string | null | undefined, districts: readonly DistrictRow[] = BERLIN_ROWS): string {
  const s = raw?.trim()
  if (!s) return ''
  const ix = indexOf(districts)
  const lower = s.toLowerCase()
  const direct = ix.byOrtsteil.get(lower) ?? ix.byBezirk.get(lower)
  if (direct) return direct
  // Spelled the way a Berlin source spells it — "Bezirk Mitte", "Berlin-
  // Kreuzberg", a Potsdam Stadtteil — `toOrtsteil` says which neighbourhood
  // that is, and the region's own rows say what it folds into. In Boston the
  // name is in no Berlin table and the answer is the empty string.
  const named = toOrtsteil(s).ortsteil
  return (named && (ix.byOrtsteil.get(named.toLowerCase()) ?? ix.byBezirk.get(named.toLowerCase()))) || ''
}

/** The groups a region's rows fold into, in the order a chip row draws them:
 *  Berlin's editorial order (`BEZIRKE`, first appearance in `ORTSTEILE`) for
 *  the names it knows — the live rows come back alphabetical by Ortsteil,
 *  which would shuffle a row Doug has seen for months — then the rest
 *  alphabetically. A row with no Bezirk is not a group (`outsideNames`). */
export function groupNames(districts: readonly DistrictRow[]): string[] {
  const names = [...new Set(districts.flatMap(d => (d.bezirk ? [d.bezirk] : [])))]
  const rank = (n: string) => { const i = BEZIRKE.indexOf(n); return i < 0 ? Infinity : i }
  return names.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b, 'de'))
}

/** Rows that are places of their own with no group above them: Berlin's
 *  Potsdam. The desk's Where list ends with them. */
export function outsideNames(districts: readonly DistrictRow[]): string[] {
  return [...new Set(districts.flatMap(d => (d.bezirk ? [] : [d.ortsteil])))]
}

export interface WhereOption { name: string; n: number }

/** The Where list: every group with something in it, alphabetically, then
 *  the places outside any group. A group at 0 is not drawn — except the one
 *  chosen, which stays so the select can show what it is set to and the way
 *  back from it. */
export function whereOptions(
  counts: ReadonlyMap<string, number>, chosen: string, districts: readonly DistrictRow[] = BERLIN_ROWS,
): WhereOption[] {
  const pick = whereOf(chosen, districts)
  const names = [...new Set(districts.flatMap(d => (d.bezirk ? [d.bezirk] : [])))].sort((a, b) => a.localeCompare(b, 'de'))
  return [...names, ...outsideNames(districts)]
    .map(name => ({ name, n: counts.get(name) ?? 0 }))
    .filter(o => o.n > 0 || o.name === pick)
}

/** The words a filter is drawn with on screen, which live with the screen. */
export interface FilterWords {
  win: (w: DeskFilters['win']) => string
  kind: (k: string) => string
  /** The narrowed-to name, once it has loaded. */
  narrow: string | null
}

/**
 * a3-25: every filter that differs from what a bare `#/browse` (or the bare
 * link the narrow came from) would show, by the name it is drawn with. One
 * list for the empty state's sentence, the standing Clear, and the folded
 * strip's count, so the three cannot disagree about what is on.
 *
 * The window is compared with the narrow's own default: a person link opens
 * on Any time, which is not a filter Doug set.
 */
export function activeFilters(
  f: DeskFilters, words: FilterWords, districts: readonly DistrictRow[] = BERLIN_ROWS,
): string[] {
  const winDefault = f.narrow ? 'any' : DESK_DEFAULTS.win
  return [
    f.narrow ? words.narrow ?? '…' : '',
    f.win !== winDefault ? words.win(f.win) : '',
    f.kind ? words.kind(f.kind) : '',
    f.district ? whereOf(f.district, districts) || f.district : '',
    f.free ? 'Free' : '',
    f.english ? 'In English' : '',
    f.q.trim() ? `“${f.q.trim()}”` : '',
  ].filter(Boolean)
}
